import { useTheme } from '@chronosphereio/chrono-ui';
import { useEffect, useMemo, useState } from 'react';
import { ContentWithLegend, useSeriesColorGenerator } from '@/components';
import { formatValue, SingleValue } from '@/core';
import { PieChartBase } from '../../../components/PieChartBase';
import { PieChartProps } from './PieChartPanel';
import { DEFAULT_AGGREGATION_PERCENT, DEFAULT_COLOR_PALETTE, DEFAULT_UNIT } from './pie-chart-model';
import { useSelectedLegendItems } from './use-selected-legend-items';
import { convertLabelFormatArrayToString, filterDatasetAndAggregatedSeries, getDatasetAndLegend } from './utils';

export interface PieChartPanelLoadedProps extends PieChartProps {
  data: SingleValue[];
}

export function PieChartPanelLoaded(props: PieChartPanelLoadedProps) {
  const {
    width,
    height,
    legend,
    aggregationPercent = DEFAULT_AGGREGATION_PERCENT,
    colorPalette = DEFAULT_COLOR_PALETTE,
    data,
    labelFormat,
    unit = DEFAULT_UNIT,
    overrides,
  } = props;

  const { theme } = useTheme();
  const seriesColorGenerator = useSeriesColorGenerator();

  const { dataset, aggregatedSeries, legendItems } = useMemo(() => {
    return getDatasetAndLegend({
      data,
      aggregationPercent,
      seriesColorGenerator,
      colorPalette,
      primaryColor: theme.palette.primary.main,
      hasLegend: legend !== undefined,
      overrides,
    });
  }, [aggregationPercent, colorPalette, data, legend, seriesColorGenerator, theme.palette.primary.main, overrides]);

  const { selectedLegendItems, setSelectedLegendItems } = useSelectedLegendItems({
    pieChartData: dataset,
  });
  const [filteredDataset, setFilteredDataset] = useState(dataset);
  const [filteredAggregatedSeries, setFilteredAggregatedSeries] = useState(aggregatedSeries);

  // if the dataset or selected legend items change, filter the dataset and aggregated series again
  useEffect(() => {
    const { filteredDataset, filteredAggregatedSeries } = filterDatasetAndAggregatedSeries({
      newSelectedLegendItems: selectedLegendItems,
      dataset,
      aggregatedSeries,
    });
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFilteredDataset(filteredDataset);
    setFilteredAggregatedSeries(filteredAggregatedSeries);
  }, [dataset, aggregatedSeries, selectedLegendItems]);

  const legendProps = useMemo(() => {
    return (
      legend &&
      legendItems && {
        options: legend,
        data: legendItems,
        selectedItems: selectedLegendItems,
        onSelectedItemsChange: setSelectedLegendItems,
        tableProps: {
          columns: [
            {
              field: 'data.value',
              headerName: 'Value',
              // Intentionally hardcoding a column width to start based on discussions
              // with design around keeping this simple to start. This may need
              // revisiting in the future to handle edge cases with very large values.
              width: 72,
              type: 'number',
              renderCell: ({ value }: { value?: number | null }) => {
                return value === null || value === undefined ? 'No data' : formatValue(value, unit);
              },
            },
          ],
        },
      }
    );
  }, [legend, legendItems, selectedLegendItems, setSelectedLegendItems, unit]);

  return (
    <ContentWithLegend
      width={width}
      height={height}
      spacing={8}
      // Making this small enough that the medium size doesn't get
      // responsive-handling-ed away when in the panel options editor.
      minChildrenHeight={50}
      legendProps={legendProps}
      legendSize={legend?.size}
    >
      {({ height, width }) => {
        return (
          <PieChartBase
            {...props}
            width={width}
            height={height}
            dataset={filteredDataset}
            aggregatedSeries={filteredAggregatedSeries}
            labelFormat={convertLabelFormatArrayToString(labelFormat)}
            unit={unit}
          />
        );
      }}
    </ContentWithLegend>
  );
}
